import {
  ChangeEvent,
  ReactNode,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormControl,
  InputLabel,
  Popover,
  TextField,
  SxProps,
  Theme,
} from '@mui/material';
import { AddIcon, CheckIcon, ChevronDownIcon } from './icons';
import { surfaceShadows } from '../../surfaceStyles';
import { minimumInteractiveSize } from './controlSizing';

interface ComboboxBaseProps<T> {
  id?: string;
  label: string;
  addLabel?: string;
  placeholder?: string;
  inputAriaLabel?: string;
  describedBy?: string;
  disabled?: boolean;
  loading?: boolean;
  busy?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  options: T[];
  pinnedOptions?: T[];
  displayValue?: ReactNode;
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionText?: (option: T) => string;
  renderOptionContent?: (option: T) => ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  groupBy?: (option: T) => string;
  getGroupLabel?: (group: string) => string;
  onSearchChange?: (query: string) => void;
  searchAction?: { label: string; onClick: () => void };
  /** Focus the search field when the list opens. Disable to avoid raising the mobile keyboard. */
  autoFocusSearch?: boolean;
  /** Multiple mode only: secondary action shown beside Done. */
  footerAction?: { label: string; onClick: () => void };
}

export type ComboboxProps<T> = ComboboxBaseProps<T> &
  (
    | { multiple: true; value: T[]; onChange: (options: T[]) => void }
    | { multiple?: false; value?: T | null; onChange: (option: T) => void }
  );

function groupOptions<T>(options: T[], groupBy?: (option: T) => string) {
  const groups = new Map<string, T[]>();
  for (const option of options) {
    const key = groupBy?.(option) || '';
    const entries = groups.get(key);
    if (entries) entries.push(option);
    else groups.set(key, [option]);
  }
  return groups;
}

function nextOptionIndex(current: number, length: number, direction: number) {
  if (length === 0) return -1;
  if (current < 0) return direction === 1 ? 0 : length - 1;
  return (current + direction + length) % length;
}

/** Searchable single or multiple selection with optional collapsible groups. */
export function Combobox<T>(props: ComboboxProps<T>) {
  const {
    id,
    label,
    addLabel,
    placeholder,
    inputAriaLabel,
    describedBy,
    disabled: disabledProp = false,
    loading = false,
    busy = false,
    size = 'small',
    sx,
    options,
    pinnedOptions = [],
    displayValue,
    getOptionKey,
    getOptionLabel,
    getOptionText = getOptionLabel,
    renderOptionContent,
    getOptionDisabled,
    groupBy,
    getGroupLabel,
    onSearchChange,
    searchAction,
    autoFocusSearch = true,
    footerAction,
  } = props;
  const disabled = disabledProp || loading;
  const anchor = useRef<HTMLDivElement>(null);
  const optionElements = useRef(new Map<string, HTMLElement>());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const panelId = useId();
  const triggerId = id || `${panelId}-trigger`;
  const values = props.multiple
    ? props.value
    : props.value == null
      ? []
      : [props.value];
  const selectedKeys = new Set(values.map(getOptionKey));
  const display =
    displayValue ??
    (values.length > 1
      ? `${values.length} selected`
      : values.length
        ? getOptionLabel(values[0])
        : '');
  const searching = query.trim().length > 0;
  const terms = query.toLocaleLowerCase().trim().split(/\s+/);
  const matches = options.filter((option) => {
    const label = getOptionLabel(option).toLocaleLowerCase();
    return terms.every((term) => label.includes(term));
  });
  const groups = groupOptions([...pinnedOptions, ...matches], groupBy);
  const visible = Array.from(groups).flatMap(([group, entries]) =>
    !searching && collapsed.has(group) ? [] : entries,
  );
  const enabled = visible.filter((option) => !getOptionDisabled?.(option));
  const active = enabled.find((option) => getOptionKey(option) === activeKey);
  const optionId = (option: T) =>
    `${panelId}-option-${encodeURIComponent(getOptionKey(option))}`;
  useEffect(() => {
    if (activeKey)
      optionElements.current
        .get(activeKey)
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeKey]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const show = () => {
    if (disabled || open) return;
    document.getElementById(triggerId)?.focus();
    setQuery('');
    onSearchChange?.('');
    setActiveKey(null);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
  };
  const choose = (option: T) => {
    if (disabled || busy || getOptionDisabled?.(option)) return;
    if (props.multiple) {
      const key = getOptionKey(option);
      props.onChange(
        selectedKeys.has(key)
          ? props.value.filter((value) => getOptionKey(value) !== key)
          : [...props.value, option],
      );
    } else {
      props.onChange(option);
      close();
    }
  };
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setActiveKey(null);
    onSearchChange?.(event.target.value);
  };
  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const current = enabled.findIndex(
        (option) => getOptionKey(option) === activeKey,
      );
      const next = nextOptionIndex(
        current,
        enabled.length,
        event.key === 'ArrowDown' ? 1 : -1,
      );
      setActiveKey(enabled[next] ? getOptionKey(enabled[next]) : null);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active) choose(active);
      else if (onSearchChange) close();
    }
  };
  const toggleGroup = (group: string) => {
    if (searching) return;
    setActiveKey(null);
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };
  return (
    <Box ref={anchor} sx={sx}>
      <FormControl fullWidth disabled={disabled}>
        {!addLabel && (
          <InputLabel id={`${triggerId}-label`} htmlFor={triggerId}>
            {label}
          </InputLabel>
        )}
        <ButtonBase
          component="button"
          type="button"
          id={triggerId}
          role="combobox"
          aria-label={inputAriaLabel || addLabel}
          aria-labelledby={
            inputAriaLabel || addLabel ? undefined : `${triggerId}-label`
          }
          aria-describedby={describedBy}
          aria-busy={loading || undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? `${panelId}-listbox` : undefined}
          disabled={disabled}
          onMouseDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.focus();
            show();
          }}
          onClick={show}
          onKeyDown={(event) => {
            if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
              event.preventDefault();
              show();
            }
          }}
          sx={{
            width: '100%',
            minWidth: 0,
            minHeight: size === 'small' ? minimumInteractiveSize : 56,
            px: 1.75,
            py: size === 'small' ? 1 : 2,
            gap: 1,
            justifyContent: 'space-between',
            textAlign: 'left',
            font: 'inherit',
            color: addLabel ? 'text.secondary' : 'text.primary',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: surfaceShadows.field,
            '&.Mui-disabled': { color: 'text.disabled' },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          {addLabel && (
            <AddIcon
              aria-hidden="true"
              sx={{ fontSize: 20, color: 'primary.main' }}
            />
          )}
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {addLabel || display}
          </Box>
          {loading ? (
            <CircularProgress
              size={20}
              aria-label={`Loading ${label.toLocaleLowerCase()}`}
              sx={{ flexShrink: 0 }}
            />
          ) : !addLabel ? (
            <ChevronDownIcon aria-hidden="true" style={{ flexShrink: 0 }} />
          ) : null}
        </ButtonBase>
      </FormControl>
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: addLabel
                ? Math.max(anchor.current?.clientWidth || 0, 280)
                : anchor.current?.clientWidth,
              maxWidth: 'calc(100vw - 32px)',
              overflow: 'hidden',
              p: 1,
            },
          },
        }}
      >
        <Box
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
        >
          <TextField
            disabled={busy}
            fullWidth
            autoFocus={autoFocusSearch}
            size={size}
            value={query}
            placeholder={placeholder || `Search ${label.toLocaleLowerCase()}…`}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            inputProps={{
              role: 'combobox',
              'aria-label': `Search ${label.toLocaleLowerCase()}`,
              'aria-expanded': true,
              'aria-autocomplete': 'list',
              'aria-controls': `${panelId}-listbox`,
              'aria-activedescendant': active ? optionId(active) : undefined,
            }}
          />
          {searchAction && (
            <Box
              sx={{ pt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Button
                startIcon={<AddIcon />}
                disabled={busy || disabled}
                onClick={searchAction.onClick}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  width: '100%',
                  overflowWrap: 'anywhere',
                }}
              >
                {searchAction.label}
              </Button>
            </Box>
          )}
          <Box
            id={`${panelId}-listbox`}
            role="listbox"
            aria-label={label}
            aria-multiselectable={props.multiple || undefined}
            onMouseMove={() => setActiveKey(null)}
            sx={{
              mt: '8px',
              maxHeight: 'min(320px, 40vh)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {Array.from(groups).map(([group, entries], index) => {
              const expanded = searching || !collapsed.has(group);
              const groupId = `${panelId}-group-${index}`;
              return (
                <Box key={group} role="presentation">
                  {group && (
                    <ButtonBase
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={groupId}
                      aria-disabled={searching}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => toggleGroup(group)}
                      sx={{
                        width: '100%',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        minHeight: 44,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textAlign: 'left',
                        cursor: searching ? 'default' : 'pointer',
                        '@media (hover: hover)': {
                          '&:hover:not([aria-disabled="true"])': {
                            bgcolor: 'action.hover',
                          },
                        },
                        '&.Mui-focusVisible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: -2,
                        },
                      }}
                    >
                      {getGroupLabel?.(group) || group}
                      <ChevronDownIcon
                        aria-hidden="true"
                        style={{
                          transform: expanded ? undefined : 'rotate(-90deg)',
                        }}
                      />
                    </ButtonBase>
                  )}
                  <Box
                    id={groupId}
                    role={group ? 'group' : 'presentation'}
                    aria-label={
                      group ? getGroupLabel?.(group) || group : undefined
                    }
                  >
                    {expanded &&
                      entries.map((option) => {
                        const key = getOptionKey(option);
                        const selected = selectedKeys.has(key);
                        const unavailable = Boolean(
                          busy || getOptionDisabled?.(option),
                        );
                        return (
                          <Box
                            key={key}
                            id={optionId(option)}
                            role="option"
                            aria-selected={selected}
                            aria-disabled={unavailable}
                            aria-label={getOptionLabel(option)}
                            ref={(element: HTMLElement | null) => {
                              if (element)
                                optionElements.current.set(key, element);
                              else optionElements.current.delete(key);
                            }}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => choose(option)}
                            sx={{
                              px: 2,
                              py: 1,
                              minHeight: 44,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: unavailable ? 'default' : 'pointer',
                              opacity: unavailable ? 0.5 : 1,
                              bgcolor:
                                key === activeKey
                                  ? 'action.focus'
                                  : selected
                                    ? 'action.selected'
                                    : 'transparent',
                              '@media (hover: hover)': {
                                '&:hover:not([aria-disabled="true"])': {
                                  bgcolor: 'action.hover',
                                },
                              },
                            }}
                          >
                            {props.multiple && (
                              <Box
                                component="span"
                                aria-hidden="true"
                                sx={{
                                  width: 24,
                                  height: 24,
                                  flexShrink: 0,
                                  color: 'primary.main',
                                }}
                              >
                                {selected && <CheckIcon fontSize="small" />}
                              </Box>
                            )}
                            {renderOptionContent?.(option) ??
                              getOptionText(option)}
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              );
            })}
            {groups.size === 0 && (
              <Box role="status" sx={{ p: 2, color: 'text.secondary' }}>
                No matching options
              </Box>
            )}
          </Box>
          {props.multiple && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: footerAction ? 'space-between' : 'flex-end',
                pt: 1,
              }}
            >
              {footerAction && (
                <Button
                  onClick={() => {
                    close();
                    footerAction.onClick();
                  }}
                >
                  {footerAction.label}
                </Button>
              )}
              <Button onClick={close}>Done</Button>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
